import * as React from 'react';

import { t } from '~/i18n';
import { Spinner, formatDateTime } from '@watcherr/ui';
import { translateRejectReason, translateResolveReason } from '~/config/alert';
import { observer } from 'mobx-react';
import Alert from '~store/Alert/Alert';
import { AlertAction, Device, AlertUpdateEvent, HcpGroup } from '@watcherr/resources';
import { useQuery } from '@tanstack/react-query';
import { useStores } from '~store/utils';

interface AlertLogsProps {
  alert: Alert;
  shortVersion?: boolean;
}
const AlertLogs: React.FC<AlertLogsProps> = observer(({ alert, shortVersion }) => {
  const { alerts } = useStores();

  React.useEffect(() => {
    alerts.fetchAlert(alert?.uuid);
  }, [alert?.uuid]);

  if (shortVersion) {
    if (alert.isCancelled) {
      return <p>{t('alarm.proximityResolveAction.cancelled.limited.description')}</p>;
    }
    if (alert.resolvedProximityAction == null) {
      //TODO: Potentially auto resolved
      return <p>{t('alarm.proximityResolveAction.on.pc')}</p>;
    }
    if (alert.resolvedProximityAction === 'SKIPPED') {
      return (
        <p>
          <strong>{t(`alarm.proximityResolveAction.skipped.limited.description`)}</strong>
        </p>
      );
    }
    return (
      <p>{t(`alarm.proximityResolveAction.${alert.resolvedProximityAction.toLowerCase()}.limited.description`)}</p>
    );
  } else {
    return (
      <ul className="list">
        <li>
          {t('alert.logs.types.create', {
            datetime: formatDateTime(alert.created),
          })}
        </li>
        {alert.alertUpdateEvents
          ?.slice()
          .sort((a, b) => {
            return a.created.getTime() - b.created.getTime();
          })
          .map((event) => (
            <AlertUpdateEventItem key={event.id} event={event} />
          ))}
        {alert.isCancelled && (
          <li>
            {t('alert.logs.types.cancel', {
              datetime: formatDateTime(alert.cancelledTime),
            })}
          </li>
        )}
      </ul>
    );
  }
});

export function AlertUpdateEventItem({ event }: { event: AlertUpdateEvent }) {
  if (!event) {
    return <Spinner label={t('global.loading')} />;
  }

  const { data: device } = useQuery({
    queryKey: ['device', event.proximityDeviceId],
    queryFn: () => Device.getDevice(event.proximityDeviceId ?? undefined),
    enabled: !!event.proximityDeviceId,
  });

  const { data: hcpGroups } = useQuery({
    queryKey: ['hcpGroups'],
    queryFn: () => HcpGroup.getHcpGroups(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  let name = event.profile ? `${event.profile.lastName.toUpperCase()} ${event.profile.firstName}` : '';
  if (event.hcpGroupId) {
    const hcpGroupName = hcpGroups?.find((g) => g.id === event.hcpGroupId)?.hcpGroupName ?? event.hcpGroupId;
    name += ` (${hcpGroupName})`;
  }
  if (event.action === AlertAction.Resolve) {
    return (
      <>
        <li key={event.id}>
          {t(`alert.logs.types.resolve`, {
            datetime: formatDateTime(event.created),
            reason: getActionReason(event),
            name: name,
          })}
        </li>
        <li key={event.id + 'prox'}>
          {event.proximityResolveAction ? (
            <p>
              {t(`alarm.proximityResolveAction.${event.proximityResolveAction.toLowerCase()}.description`)}
              <strong>{device ? ` ${device?.macAddress ?? '-'} ${device?.description ?? '-'}` : ''}</strong>
            </p>
          ) : event.profile?.lastName == 'System' ? (
            <p>{t(`alarm.autoResolve`)}</p>
          ) : (
            <p>{t(`alarm.proximityResolveAction.on.pc`)}</p>
          )}
        </li>
      </>
    );
  }
  return (
    <li key={event.id}>
      {t(`alert.logs.types.${event.action.toLowerCase()}`, {
        datetime: formatDateTime(event.created),
        reason: getActionReason(event),
        name: name,
      })}
    </li>
  );
}

function getActionReason(event: AlertUpdateEvent) {
  if (!event.reason) return undefined;
  if (event.action === AlertAction.Resolve) return translateResolveReason(event.reason) ?? '-';
  if (event.action === AlertAction.Reject) return translateRejectReason(event.reason) ?? '-';
  return undefined;
}

export default AlertLogs;
